import axios, { AxiosProgressEvent, AxiosResponse } from 'axios';
import { store } from '../stores/store';
import { User, UserFormValues } from '../models/User';
import { ShopifyAuth } from '../models/ShopifyAuth';
import { UserSetting } from '../models/UserSetting';
import { InventoryLocationOption } from '../models/InventoryLocation';
import { VendorSkuRule } from '../models/VendorSkuRule';
import { ShopifySyncLog } from '../models/ShopifySyncLog';
import { Group, GroupFormValues } from '../models/Group';
import { PricePullHistory, Pricing } from '../models/Pricing';
import { ProductDetails, ProductListResult } from '../models/Product';
import { VehicleListResult, VehicleCreate, VehicleEdit, VehicleFilterOptionsResult } from '../models/Vehicle';
import { AdminDataFeedRunLog, AdminFitmentImportLog, AdminLoginResponse, AdminPagedResult, AdminShopRuntime, AdminStoreDetails, AdminStoreGeneralSettings, AdminStoreNote, AdminStoreSummary, AdminStoreSyncError, AdminStoreSyncLog, AdminSyncLogErrorsResponse, AdminWebhookLog } from '../models/Admin';

//axios.defaults.baseURL = 'http://localhost:5000/api';
//axios.defaults.baseURL = 'https://rh.ngrok.app/api';
axios.defaults.baseURL = process.env.REACT_APP_API_URL;

const adminTokenStorageKey = "partfinder.admin.token";
const previewTokenStorageKey = "partfinder.preview.token";

// this sends bearer token (if available) with each request
axios.interceptors.request.use(config => {
    const token = store.commonStore.token;
    if (token) config.headers.Authorization = `Bearer ${token}`
    return config;
})

// Used to create fake delay, for testing loading states
const sleep = (delay: number) => {
    return new Promise((resolve) => {
        setTimeout(resolve, delay);
    })
}

// Used to create fake delay, for testing loading states
axios.interceptors.response.use(async response => {
    try {
        const requestUrl = response.config?.url ?? "";
        const refreshedToken = response.headers?.["x-session-token"];
        if (typeof refreshedToken === "string" && refreshedToken.trim().length > 0) {
            if (requestUrl.startsWith("/pf-console")) {
                localStorage.setItem(adminTokenStorageKey, refreshedToken);
            } else if (requestUrl.startsWith("/pf-preview")) {
                localStorage.setItem(previewTokenStorageKey, refreshedToken);
            }
        }

        //await sleep(500);
        return response;
    } catch (error) {
        console.log(error);
        return await Promise.reject(error);
    }
}, error => {
    const { status } = error.response || {};
    if (status === 401 || status === 403 || status === 404) {
        store.userStore?.logout?.();
    }
    return Promise.reject(error);
});

const responseBody = <T> (response: AxiosResponse<T>) => response.data;

const requests = {
    get: <T> (url: string) => axios.get<T>(url).then(responseBody),
    post: <T> (url: string, body: {}) => axios.post<T>(url, body).then(responseBody),
    put: <T> (url: string, body: {}) => axios.put<T>(url, body).then(responseBody),
    del: <T> (url: string) => axios.delete<T>(url).then(responseBody),
}

const appendShopOverride = (url: string, shopIdOverride?: string | number) => {
    if (shopIdOverride === undefined || shopIdOverride === null || shopIdOverride === "") {
        return url;
    }

    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}shopIdOverride=${encodeURIComponent(shopIdOverride.toString())}`;
};

const Account = {
    auth: (user: UserFormValues) => requests.post<string>('/account/auth', user),
    embeddedauth: (user: ShopifyAuth) => requests.post<string>('/account/embeddedauth', user),
    embeddedlogin: (user: ShopifyAuth) => requests.post<User>('/account/embeddedlogin', user),
    login: (user: ShopifyAuth) => requests.post<User>('/account/login', user),
    current: () => requests.get<User>('/account'),
    getSubscriptionStatus: () => requests.get<{ hasActiveSubscription: boolean }>('/account/subscription/status'),

}

const Settings = {
    details: (shopIdOverride?: string | number) => requests.get<UserSetting>(appendShopOverride('/settings', shopIdOverride)),
    update: (setting: UserSetting, shopIdOverride?: string | number) => axios.put<void>(appendShopOverride('/settings', shopIdOverride), setting),
    inventoryLocations: (shopIdOverride?: string | number) => requests.get<InventoryLocationOption[]>(appendShopOverride('/settings/inventory/locations', shopIdOverride)),
    vendorSkuRules: (shopIdOverride?: string | number) => requests.get<VendorSkuRule[]>(appendShopOverride('/settings/vendor-sku-rules', shopIdOverride)),
    updateVendorSkuRules: (rules: VendorSkuRule[], shopIdOverride?: string | number) => requests.put<VendorSkuRule[]>(appendShopOverride('/settings/vendor-sku-rules', shopIdOverride), rules),
}

const SyncLogInfo = {
    details: () => requests.get<ShopifySyncLog>('/synclog/details'),
    list: () => requests.get<ShopifySyncLog[]>('/synclog'),  
}


const PricingInfo = {
    details: () => requests.get<Pricing>('/pricing'),
    restore: (pricing: PricePullHistory) => axios.post<PricePullHistory>(`/pricing/`, pricing),
}

const Groups = {
    list: () => requests.get<Group[]>('/groups'),
    details: (id: string) => requests.get<Group>(`/groups/${id}`),
    create: (group: GroupFormValues) => axios.post<void>('/groups', group),
    update: (group: GroupFormValues) => axios.put<void>(`/groups/${group.id}`, group),
    delete: (id: string) => axios.delete<void>(`/groups/${id}`)
}

const Products = {
    list: (
        page: number,
        query: string = '',
        hasFitment: string = '',
        vendor: string = '',
        tag: string = '',
        sort: string = 'title asc',
        year: string = '',
        make: string = '',
        model: string = '',
        mappedOnly: boolean = false
    ) => {
        const params = new URLSearchParams({
            page: page.toString(),
            query,
            hasFitment,
            vendor,
            tag,
            sort,
            year,
            make,
            model,
            mappedOnly: mappedOnly ? 'true' : 'false',
        });
        return requests.get<ProductListResult>(`/products?${params.toString()}`);
    },
    bulkFitments: (payload: any) => requests.post<void>('/products/bulk-fitments', payload),
    details: (id: number) => requests.get<ProductDetails>(`/products/${id}`),
    addFitment: (id: number, payload: { year: string; make: string; model: string }) =>
        requests.post<void>(`/products/${id}/fitments`, payload),
    removeFitment: (id: number, fitmentId: number) => requests.del<void>(`/products/${id}/fitments/${fitmentId}`)
}

const Vehicles = {
    list: (page: number, query: string = '', source: string = '', sort: string = 'year desc') => {
        const params = new URLSearchParams({
            page: page.toString(),
            query,
            source,
            sort,
        });
        return requests.get<VehicleListResult>(`/vehicles?${params.toString()}`);
    },
    filterOptions: (year: string = '', make: string = '', source: string = '') => {
        const params = new URLSearchParams({ year, make, source });
        return requests.get<VehicleFilterOptionsResult>(`/vehicles/filter-options?${params.toString()}`);
    },
    create: (vehicle: VehicleCreate) => requests.post<void>('/vehicles', vehicle),
    update: (id: number, vehicle: VehicleEdit) => requests.put<void>(`/vehicles/${id}`, vehicle),
    delete: (id: number) => requests.del<void>(`/vehicles/${id}`),
    bulkDelete: (payload: any) => requests.post<void>('/vehicles/bulk-delete', payload),
}

export type ImportFormat = 'csv_xlsx' | 'aces_xml';

export type UploadProgressHandler = (event: AxiosProgressEvent) => void;

export interface FitmentImportHistoryItem {
    queueId: number;
    originalFileName: string;
    storedFileName: string;
    importFormat: ImportFormat | 'unknown';
    state: string;
    taggingStatus?: string;
    processingStage?: string;
    processingMessage?: string;
    progressPercent?: number;
    error?: string;
    addedOn: string;
    startedAt?: string;
    completedAt?: string;
    attemptCount?: number;
    parsedRecordCount?: number;
    appliedRecordCount?: number;
    declaredRecordCount?: number;
    isArchived: boolean;
    archivedAt?: string;
    restrictToVendor: boolean;
    selectedVendorName?: string;
    vendorSkuSuffixRule?: string;
    normalizeSku?: boolean;
    useVendorSkuSuffixRule: boolean;
    updateSavedVendorSkuSuffixRule: boolean;
}
export interface FitmentImportQueueResult {
    queueId: number;
    storedFileName: string;
    originalFileName: string;
    importFormat: ImportFormat;
    acesSetup?: AcesImportSetup;
}

export interface AcesImportSetup {
    company?: string;
    documentTitle?: string;
    brandAAIAID?: string;
    submissionType?: string;
    effectiveDate?: string;
    transferDate?: string;
    vendorOptions: string[];
    matchedVendor?: string;
    vendorSkuSuffixRule?: string;
    normalizeSku?: boolean;
}

export interface ConfigureAcesImportPayload {
    selectedVendor?: string;
    skuSuffixRule?: string;
    useSkuSuffixRule: boolean;
    updateVendorRule: boolean;
    normalizeSku: boolean;
}


const Admin = {
    login: (email: string, password: string) => requests.post<AdminLoginResponse>('/pf-console/login', { email, password }),
    stores: (token: string, query: string = '', page: number = 1, pageSize: number = 50, isActive?: boolean) => {
        const params = new URLSearchParams({
            query,
            page: String(page),
            pageSize: String(pageSize)
        });

        if (isActive !== undefined) {
            params.set('isActive', String(isActive));
        }

        return axios.get<AdminPagedResult<AdminStoreSummary>>(`/pf-console/stores?${params.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody);
    },
    storeDetails: (token: string, shopId: number) =>
        axios.get<AdminStoreDetails>(`/pf-console/stores/${shopId}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    shopRuntime: (token: string, shopId: number) =>
        axios.get<AdminShopRuntime>(`/pf-console/stores/${shopId}/shopify-runtime`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    deleteWebhookSubscription: (token: string, shopId: number, id: string) =>
        axios.delete<void>(`/pf-console/stores/${shopId}/webhook-subscriptions?id=${encodeURIComponent(id)}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    syncLogs: (token: string, page: number = 1, pageSize: number = 50) =>
        axios.get<AdminPagedResult<AdminStoreSyncLog>>(`/pf-console/sync-logs?page=${page}&pageSize=${pageSize}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    syncLogErrors: (token: string, syncLogId: number) =>
        axios.get<AdminSyncLogErrorsResponse>(`/pf-console/sync-logs/${syncLogId}/errors`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    deleteSyncLog: (token: string, syncLogId: number) =>
        axios.delete<void>(`/pf-console/sync-logs/${syncLogId}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    errorLogs: (token: string, page: number = 1, pageSize: number = 50) =>
        axios.get<AdminPagedResult<AdminStoreSyncError>>(`/pf-console/error-logs?page=${page}&pageSize=${pageSize}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    webhookLogs: (token: string, page: number = 1, pageSize: number = 50, shopId?: number, topic?: string, processedSuccessfully?: boolean) => {
        const query = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
        if (shopId !== undefined) query.set('shopId', String(shopId));
        if (topic) query.set('topic', topic);
        if (processedSuccessfully !== undefined) query.set('processedSuccessfully', String(processedSuccessfully));

        return axios.get<AdminPagedResult<AdminWebhookLog>>(`/pf-console/webhook-logs?${query.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody);
    },
    dataFeedLogs: (token: string, page: number = 1, pageSize: number = 50, shopId?: number, status?: string) => {
        const query = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
        if (shopId !== undefined) query.set('shopId', String(shopId));
        if (status) query.set('status', status);

        return axios.get<AdminPagedResult<AdminDataFeedRunLog>>(`/pf-console/data-feed-logs?${query.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody);
    },
    fitmentImportLogs: (token: string, page: number = 1, pageSize: number = 50, shopId?: number, state?: string) => {
        const query = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
        if (shopId !== undefined) query.set('shopId', String(shopId));
        if (state) query.set('state', state);

        return axios.get<AdminPagedResult<AdminFitmentImportLog>>(`/pf-console/fitment-import-logs?${query.toString()}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody);
    },
    clearErrorLogs: (token: string) =>
        axios.delete<void>(`/pf-console/error-logs`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    updateStoreActiveStatus: (token: string, shopId: number, isActive: boolean) =>
        axios.put<AdminStoreSummary>(`/pf-console/stores/${shopId}/isactive`, { isActive }, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    updateStoreSyncSetting: (token: string, shopId: number, key: string, value: string) =>
        axios.put<AdminStoreSummary>(`/pf-console/stores/${shopId}/sync-settings`, { key, value }, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    updateStoreGeneralSettings: (token: string, shopId: number, payload: AdminStoreGeneralSettings) =>
        axios.put<AdminStoreGeneralSettings>(`/pf-console/stores/${shopId}/general-settings`, payload, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    clearStoreSurgeMode: (token: string, shopId: number) =>
        axios.post<AdminStoreSummary>(`/pf-console/stores/${shopId}/clear-surge-mode`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    downloadStoreFitmentImportFile: (token: string, shopId: number, queueId: number) =>
        axios.get<Blob>(`/pf-console/stores/${shopId}/fitment-imports/${queueId}/file`, {
            responseType: 'blob',
            headers: { Authorization: `Bearer ${token}` }
        }),
    deleteStoreFitmentImportHistoryItem: (token: string, shopId: number, queueId: number) =>
        axios.delete<void>(`/pf-console/stores/${shopId}/fitment-imports/${queueId}`, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
    addStoreNote: (token: string, shopId: number, note: string) =>
        axios.post<AdminStoreNote>(`/pf-console/stores/${shopId}/notes`, { note }, {
            headers: { Authorization: `Bearer ${token}` }
        }).then(responseBody),
}


const Preview = {
    login: (email: string, password: string) => requests.post<AdminLoginResponse>('/pf-preview/login', { email, password }),
}

const PreviewImports = {
    history: (token: string) => axios.get<FitmentImportHistoryItem[]>('/pf-preview/fitment-imports', { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
    archive: (token: string, queueId: number) => axios.post<void>(`/pf-preview/fitment-imports/${queueId}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } }),
    queue: (token: string, file: File, onUploadProgress?: UploadProgressHandler) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('importFormat', 'aces_xml');

        return axios.post<FitmentImportQueueResult>('/pf-preview/fitment-imports', formData, {
            headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` },
            onUploadProgress
        }).then(responseBody);
    },
    configureAces: (token: string, queueId: number, payload: ConfigureAcesImportPayload) => axios.post<void>(`/pf-preview/fitment-imports/${queueId}/aces-setup`, payload, { headers: { Authorization: `Bearer ${token}` } }),
    download: (token: string, queueId: number) => axios.get<Blob>(`/pf-preview/fitment-imports/${queueId}/file`, { responseType: 'blob', headers: { Authorization: `Bearer ${token}` } }),
    vendorSkuRules: (token: string) => axios.get<VendorSkuRule[]>('/settings/vendor-sku-rules', { headers: { Authorization: `Bearer ${token}` } }).then(responseBody),
}

const Imports = {
    history: () => requests.get<FitmentImportHistoryItem[]>('/fitmentimports'),
    archive: (queueId: number) => axios.post<void>(`/fitmentimports/${queueId}/archive`, {}),
    queue: (file: File, importFormat: ImportFormat, onUploadProgress?: UploadProgressHandler) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('importFormat', importFormat);

        return axios.post<FitmentImportQueueResult>('/fitmentimports', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            onUploadProgress
        }).then(responseBody);
    },
    configureAces: (queueId: number, payload: ConfigureAcesImportPayload) => axios.post<void>(`/fitmentimports/${queueId}/aces-setup`, payload),
    download: (queueId: number) => axios.get<Blob>(`/fitmentimports/${queueId}/file`, { responseType: 'blob' })
}

const agent = {
    Account,
    Settings,
    Groups,
    SyncLogInfo,
    PricingInfo,
    Products,
    Vehicles,
    Admin,
    Preview,
    PreviewImports,
    Imports
}
 
export default agent;
